<script>
  import Icon from "@iconify/svelte";
  import { t } from "../lib/i18n";
  import apiIcon from "@iconify/icons-mdi/api";
  import lockIcon from "@iconify/icons-mdi/lock-outline";
  import keyIcon from "@iconify/icons-mdi/key-outline";
  import shieldIcon from "@iconify/icons-mdi/shield-key-outline";
  import informationIcon from "@iconify/icons-mdi/information-outline";

  export let apiBase = "";
  export let apiKey = ""; // NEW (for docs + copy/paste)

  // Prefer server-provided host if present; fallback to browser hostname.
  const hostname =
    typeof window !== "undefined" && window?.location?.host
      ? window.location.host
      : "";

  const baseLabel = apiBase || "(same-origin)";
  const fullBase = apiBase || (hostname ? `${location.protocol}//${hostname}` : "(same-origin)");

  const mono =
    "font-mono text-xs text-zinc-200 whitespace-nowrap overflow-hidden text-ellipsis";

  const codePill =
    "inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1 font-mono text-xs text-zinc-200";

  const sectionCard =
    "rounded-2xl border border-white/10 bg-black/20 p-4 sm:p-5";

  const klabel = "text-xs text-zinc-400 flex flex-wrap items-center gap-2";
  const khint =
    "text-[11px] text-zinc-500 inline-flex items-center gap-1 whitespace-nowrap";

  const badge =
    "inline-flex items-center rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1 text-[11px] text-zinc-300";

  const methodBadge = (m) => {
    if (m === "GET")
      return "border-emerald-400/30 bg-emerald-500/10 text-emerald-100";
    if (m === "POST")
      return "border-sky-400/30 bg-sky-500/10 text-sky-100";
    return "border-white/10 bg-white/[0.03] text-zinc-200";
  };

  $: routes = [
    {
      method: "GET",
      path: "/up",
      desc: $t('api.liveness_probe'),
      returns: `"ok" (text/plain)`,
      notes: []
    },
    {
      method: "GET",
      path: "/setup",
      desc: $t('api.env_config_snapshot'),
      returns: "JSON: { meta, env[], config }",
      notes: []
    },
    {
      method: "GET",
      path: "/tail",
      desc: $t('api.last_100_log'),
      returns: "JSON: { limit, order, lines[] }",
      notes: []
    },
    {
      method: "GET",
      path: "/",
      desc: $t('api.root_route_desc'),
      returns: "HTML (UI) or JSON worker snapshot",
      notes: []
    },
    {
      method: "POST",
      path: "/push",
      desc: $t('api.queue_new_job'),
      returns: "JSON: { job_id }",
      notes: []
    },
    {
      method: "GET",
      path: "/results",
      desc: $t('api.paginated_results'),
      returns: "JSON list + paging info",
      notes: []
    },
    {
      method: "GET",
      path: "/results/{job_id}",
      desc: $t('api.fetch_result_detail'),
      returns: "JSON: JobResult",
      notes: []
    },
    {
      method: "GET",
      path: "/webhooks",
      desc: $t('api.webhook_history'),
      returns: "JSON: list of webhook attempts grouped by job/url/event",
      notes: []
    },
    {
      method: "GET",
      path: "/storage",
      desc: $t('api.list_storage_root'),
      returns: "JSON listing (recursive/flat per implementation)",
      notes: []
    },
    {
      method: "GET",
      path: "/storage/{path...}",
      desc: $t('api.list_storage_subpath'),
      returns: "JSON listing",
      notes: []
    }
  ];

  const headers = [
    {
      k: "Accept",
      v: "application/json",
      hint: "Required for XHR; forces JSON responses"
    },
    { k: "Content-Type", v: "application/json", hint: "Required for POST /push" }
  ];

  // Auth docs (mirrors your middleware behavior):
  // - JSON routes (detected via Accept header) use Bearer token when SHARED_API_SECRET is set.
  // - UI routes use Basic auth when MONOCLE_USERNAME + MONOCLE_PASSWORD are set.
  const authHeaders = [
    {
      kind: "Bearer",
      header: "Authorization",
      value: "Bearer <SHARED_API_KEY>",
      when: "Only for JSON routes when SHARED_API_SECRET is set",
      env: ["SHARED_API_SECRET", "SHARED_API_KEY"]
    },
    {
      kind: "Basic",
      header: "Authorization",
      value: "Basic <base64(MONOCLE_USERNAME:MONOCLE_PASSWORD)>",
      when: "Only for UI/HTML routes when both UI creds are set",
      env: ["MONOCLE_USERNAME", "MONOCLE_PASSWORD"]
    }
  ];

  const pushExample = `{
  "input_key": "documents/input/test-document.pdf",
  "output_key": "documents/output/test-output-folder",
  "document_id": "12345",
  "tenant_id": "demo",
  "webhook": "https://example.com/webhook" // optional helper in UI
}`;

  // Curl examples
  const curlPush = `curl -sS "${fullBase}/push" \\
  -H "Accept: application/json" \\
  -H "Content-Type: application/json" \\
  # -H "Authorization: Bearer <SHARED_API_KEY>" \\
  -d '${pushExample.replaceAll("'", "\\'")}'`;

  const curlResults = `curl -sS "${fullBase}/results" \\
  -H "Accept: application/json" \\
  # -H "Authorization: Bearer <SHARED_API_KEY>"`;

  const curlResultOne = `curl -sS "${fullBase}/results/<job_id>" \\
  -H "Accept: application/json" \\
  # -H "Authorization: Bearer <SHARED_API_KEY>"`;

  const curlStorage = `curl -sS "${fullBase}/storage" \\
  -H "Accept: application/json" \\
  # -H "Authorization: Bearer <SHARED_API_KEY>"`;

  const curlWebhooks = `curl -sS "${fullBase}/webhooks" \\
  -H "Accept: application/json" \\
  # -H "Authorization: Bearer <SHARED_API_KEY>"`;

  const curlUI = `# UI (HTML) request — do NOT send Accept: application/json
curl -sS "${fullBase}/" \\
  -u "<MONOCLE_USERNAME>:<MONOCLE_PASSWORD>"`;

  const curlUIAssets = `# UI assets (same rule)
curl -sS "${fullBase}/assets/" \\
  -u "<MONOCLE_USERNAME>:<MONOCLE_PASSWORD>"`;
</script>

<main class="w-full px-4 py-6">
  <section class="ui-card p-5 sm:p-6">
    <!-- Header -->
    <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div class="flex items-start gap-3">
        <span
          class="inline-flex h-9 w-9 items-center justify-center rounded border border-teal-400/40 bg-teal-500/25 text-teal-200"
        >
          <Icon icon={apiIcon} width="18" height="18" />
        </span>

        <div>
          <h1 class="text-lg font-normal tracking-wide text-zinc-100">
            {$t('api.title')}
          </h1>
          <p class="mt-0.5 text-sm text-zinc-400">
            {$t('api.subtitle')}
          </p>
        </div>
      </div>

      <div class="flex flex-wrap gap-2">
        <div
          class="inline-flex items-center rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1 text-[11px] text-zinc-300"
        >
          {$t('common.host')}:
          <span class="ml-1 font-mono text-zinc-200">
            {hostname || "—"}
          </span>
        </div>

        <div
          class="inline-flex items-center rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1 text-[11px] text-zinc-300"
        >
          {$t('common.base')}:
          <span class="ml-1 font-mono text-zinc-200">
            {baseLabel}
          </span>
        </div>
      </div>
    </div>

    <!-- Authentication -->
    <div class="mt-6 rounded-2xl border border-white/10 bg-black/20 p-4 sm:p-5">
      <div class="flex items-baseline justify-between gap-3 border-b border-white/10 pb-3">
        <div class="flex items-center gap-2 text-sm font-semibold tracking-wide text-white">
          <Icon icon={lockIcon} width="16" height="16" />
          {$t('api.authentication')}
        </div>
        <div class="text-xs text-zinc-500">{$t('common.optional')}</div>
      </div>

      <div class="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
        <div class="rounded-xl border border-white/10 bg-white/[0.03] p-4">
          <div class="flex items-center gap-2 text-xs text-zinc-300">
            <Icon icon={shieldIcon} width="16" height="16" />
            <span class="font-semibold text-zinc-200">{$t('api.bearer_title')}</span>
            <span class={badge}>API</span>
          </div>

          <p class="mt-2 text-xs text-zinc-400 leading-relaxed">
            {@html $t('api.bearer_desc')}
          </p>

          <div class="mt-3 text-xs text-zinc-400">
            {$t('api.header_label')}
            <span class="ml-2 font-mono text-zinc-200">Authorization: Bearer &lt;SHARED_API_KEY&gt;</span>
          </div>

          <div class="mt-3 text-xs text-zinc-400">
            {$t('api.your_current_key')}
            <span class="ml-2 font-mono text-zinc-200">
              {apiKey ? $t('common.set') : $t('common.not_set')}
            </span>
          </div>

          <div class="mt-3 text-xs text-zinc-400">{$t('common.example')}</div>
          <pre class="mt-2 rounded-xl border border-white/10 bg-black/30 p-3 text-xs text-zinc-200 overflow-auto">{curlResults}</pre>

          <div class="mt-3 flex flex-wrap gap-2">
            <span class={badge}>env: SHARED_API_SECRET</span>
            <span class={badge}>env: SHARED_API_KEY</span>
          </div>
        </div>

        <div class="rounded-xl border border-white/10 bg-white/[0.03] p-4">
          <div class="flex items-center gap-2 text-xs text-zinc-300">
            <Icon icon={keyIcon} width="16" height="16" />
            <span class="font-semibold text-zinc-200">{$t('api.basic_title')}</span>
            <span class={badge}>UI</span>
          </div>

          <p class="mt-2 text-xs text-zinc-400 leading-relaxed">
            {@html $t('api.basic_desc')}
            <span class="text-zinc-500">{$t('api.basic_note')}</span> {@html $t('api.basic_json_note')}
          </p>

          <div class="mt-3 text-xs text-zinc-400">
            {$t('api.curl_label')}
            <span class="ml-2 font-mono text-zinc-200">-u "&lt;MONOCLE_USERNAME&gt;:&lt;MONOCLE_PASSWORD&gt;"</span>
          </div>

          <div class="mt-3 text-xs text-zinc-400">{$t('common.example')}</div>
          <pre class="mt-2 rounded-xl border border-white/10 bg-black/30 p-3 text-xs text-zinc-200 overflow-auto">{curlUI}</pre>

          <div class="mt-3 flex flex-wrap gap-2">
            <span class={badge}>env: MONOCLE_USERNAME</span>
            <span class={badge}>env: MONOCLE_PASSWORD</span>
          </div>
        </div>
      </div>

      <div class="mt-4 rounded-xl border border-white/10 bg-black/20 p-4">
        <div class="flex items-start gap-2">
          <Icon icon={informationIcon} width="16" height="16" />
          <div class="min-w-0">
            <div class="text-xs font-semibold text-zinc-200">{$t('api.routing_title')}</div>
            <div class="mt-1 text-xs text-zinc-400 leading-relaxed">
              {@html $t('api.routing_desc')}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Required headers -->
    <div class="mt-4 rounded-2xl border border-white/10 bg-black/20 p-4 sm:p-5">
      <div class="flex items-baseline justify-between gap-3 border-b border-white/10 pb-3">
        <div class="text-sm font-semibold tracking-wide text-white">
          {$t('api.required_headers')}
        </div>
        <div class="text-xs text-zinc-500">
          {headers.length} {$t('common.items')}
        </div>
      </div>

      <div class="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
        {#each headers as h (h.k)}
          <div class="rounded-xl border border-white/10 bg-white/[0.03] p-3 min-w-0">
            <div class="text-xs text-zinc-400">
              <span class="font-medium text-zinc-200">{h.k}</span>
              <span class="ml-2 text-[11px] text-zinc-500">• {h.hint}</span>
            </div>
            <div
              class="mt-2 font-mono text-xs text-zinc-200 whitespace-nowrap overflow-hidden text-ellipsis"
              title={h.v}
            >
              {h.v}
            </div>
          </div>
        {/each}
      </div>
    </div>

    <!-- Routes -->
    <div class="mt-4 rounded-2xl border border-white/10 bg-black/20 p-4 sm:p-5">
      <div class="flex items-baseline justify-between gap-3 border-b border-white/10 pb-3">
        <div class="text-sm font-semibold tracking-wide text-white">
          {$t('api.routes')}
        </div>
        <div class="text-xs text-zinc-500">
          {$t('api.routes_count', { count: routes.length })}
        </div>
      </div>

      <div class="mt-4 flex flex-col gap-3">
        {#each routes as rt (rt.method + rt.path)}
          <div class="flex flex-col gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-4 sm:p-5 sm:flex-row sm:items-start sm:gap-6">
            <!-- Left: method + path -->
            <div class="flex shrink-0 items-center gap-2 sm:w-52">
              <span
                class={`inline-flex items-center justify-center rounded-lg border px-2 py-1 text-[11px] font-semibold ${methodBadge(
                  rt.method
                )}`}
              >
                {rt.method}
              </span>

              <span class="font-mono text-xs text-zinc-200 whitespace-nowrap" title={rt.path}>
                {rt.path}
              </span>
            </div>

            <!-- Right: description (+ notes inline) -->
            <div class="min-w-0 flex-1">
              <div class="text-sm text-zinc-200 leading-snug">
                {rt.desc}
              </div>

              <div class="mt-1 text-xs text-zinc-400">
                <span class="text-zinc-500">{$t('common.returns')}</span> {rt.returns}
              </div>

              {#if rt.notes?.length}
                <div class="mt-2 flex flex-wrap gap-2">
                  {#each rt.notes as n (n)}
                    <span class="rounded-lg border border-white/10 bg-black/20 px-2 py-1 text-[11px] text-zinc-400">
                      {n}
                    </span>
                  {/each}
                </div>
              {/if}
            </div>
          </div>
        {/each}
      </div>
    </div>

    <!-- Examples -->
    <div class="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-2">
      <!-- POST /push -->
      <div class="rounded-2xl border border-white/10 bg-black/20 p-4 sm:p-5">
        <div class="flex items-baseline justify-between gap-3 border-b border-white/10 pb-3">
          <div class="text-sm font-semibold tracking-wide text-white">
            {$t('api.post_push')}
          </div>
          <div class="text-xs text-zinc-500">{$t('common.example')}</div>
        </div>

        <div class="mt-4">
          <div class="text-xs text-zinc-400">{$t('api.body')}</div>
          <pre class="mt-2 rounded-xl border border-white/10 bg-black/30 p-3 text-xs text-zinc-200 overflow-auto">{pushExample}</pre>

          <div class="mt-4 text-xs text-zinc-400">curl</div>
          <pre class="mt-2 rounded-xl border border-white/10 bg-black/30 p-3 text-xs text-zinc-200 overflow-auto">{curlPush}</pre>
        </div>
      </div>

      <!-- Browse -->
      <div class="rounded-2xl border border-white/10 bg-black/20 p-4 sm:p-5">
        <div class="flex items-baseline justify-between gap-3 border-b border-white/10 pb-3">
          <div class="text-sm font-semibold tracking-wide text-white">
            {$t('api.browse_data')}
          </div>
          <div class="text-xs text-zinc-500">{$t('common.examples')}</div>
        </div>

        <div class="mt-4 space-y-4">
          <div>
            <div class="text-xs text-zinc-400">{$t('nav.results')}</div>
            <pre class="mt-2 rounded-xl border border-white/10 bg-black/30 p-3 text-xs text-zinc-200 overflow-auto">{curlResults}</pre>
          </div>

          <div>
            <div class="text-xs text-zinc-400">{$t('api.single_result')}</div>
            <pre class="mt-2 rounded-xl border border-white/10 bg-black/30 p-3 text-xs text-zinc-200 overflow-auto">{curlResultOne}</pre>
          </div>

          <div>
            <div class="text-xs text-zinc-400">{$t('nav.webhooks')}</div>
            <pre class="mt-2 rounded-xl border border-white/10 bg-black/30 p-3 text-xs text-zinc-200 overflow-auto">{curlWebhooks}</pre>
          </div>

          <div>
            <div class="text-xs text-zinc-400">{$t('nav.storage')}</div>
            <pre class="mt-2 rounded-xl border border-white/10 bg-black/30 p-3 text-xs text-zinc-200 overflow-auto">{curlStorage}</pre>
          </div>

          <div>
            <div class="text-xs text-zinc-400">UI</div>
            <pre class="mt-2 rounded-xl border border-white/10 bg-black/30 p-3 text-xs text-zinc-200 overflow-auto">{curlUIAssets}</pre>
          </div>
        </div>
      </div>
    </div>
  </section>
</main>
